import consolv from 'lib/consolv';

const defaults = {
  fixedLeaderboard: {
    enabled: false,
    duration: 10,
    resizeWaitDuration: 5,
    containerSelector: '#leaderboard-wrap',
    leaderboardSelector: '#ad_page_top, #ad_page_top_reform',
    containerBackgroundColor: '#f5f5f5',
    position: 'top',
    maximumHeight: 90,
    pageLoadOnly: true,
    sizes: [],
  },
};

const generateStyles = values =>
  `${values.containerSelector}.fixed-leaderboard {
    z-index: 10000;
    position: fixed;
    ${values.position}: 0;
    width: 100%;
    overflow: hidden;
    transition: height 0.5s;
    background-color: ${values.containerBackgroundColor};
  }`;

export default class FixedLeaderboard {
  _MCP;

  _configuration;

  captured;

  /**
   * Modifies the surrounding styling of ad slots to allow affixing them to the viewport for a
   * specified duration of time
   * @param {MCPInstance} MCP - Reference to the MCP instance
   */
  constructor(MCP) {
    this.initialize = this.initialize.bind(this);
    this.handleSlotCreativeLoaded = this.handleSlotCreativeLoaded.bind(this);
    this.captureLeaderboard = this.captureLeaderboard.bind(this);
    this.releaseLeaderboard = this.releaseLeaderboard.bind(this);

    this._MCP = MCP;

    // MCP.addEventListener('loaded', this.initialize);
    MCP.loaded.then(this.initialize);
    MCP.addEventListener('slotCreativeLoaded', this.handleSlotCreativeLoaded);
    MCP.addEventListener('createiveMetaRetrieved', this.handleSlotCreativeLoaded);
  }

  initialize() {
    this._configuration =
      this._MCP.Configurator.getConfiguration({ defaults }).fixedLeaderboard;

    const styleNode = document.createElement('style');
    const { containerSelector, containerBackgroundColor, position } = this._configuration;

    styleNode.innerHTML = generateStyles({
      containerSelector,
      containerBackgroundColor,
      position,
    });

    document.body.appendChild(styleNode);
  }

  handleSlotCreativeLoaded({ slot }) {
    const {
      enabled,
      containerSelector,
      leaderboardSelector,
      resizeWaitDuration,
      maximumHeight,
      pageLoadOnly,
      sizes,
    } = this._configuration;

    const leaderboardContainer = document.querySelector(containerSelector);
    const leaderboard = document.querySelector(leaderboardSelector);

    if (!leaderboardContainer || !leaderboard || slot.element !== leaderboard) return;
    if (!enabled) return;
    // if (slot.meta.creativeMetaLoaded === false) return;
    // if (slot.meta.enableFixedLeaderboard !== true) return;

    const creativeSize = slot?.creative?.size;
    if (Array.isArray(sizes) && sizes.length &&
      (!creativeSize || sizes.indexOf(creativeSize)) < 0) return;

    // Stop monitoring ad slots after finding the first leaderboard load, if configured
    if (pageLoadOnly) {
      this._MCP.removeEventListener('slotCreativeLoaded', this.handleSlotCreativeLoaded);
    }

    const leaderboardHeight = leaderboard.offsetHeight;

    // Only fix leaderboards if they are within the maximum height allowed
    if (leaderboardHeight > maximumHeight) return;

    let timerId;
    let elapsed = 0;

    // Wait for it to adjust it's size before deciding whether to capture it
    const waitForHeight = () => {
      if (leaderboard.offsetHeight > 1) {
        if (leaderboard.offsetHeight <= maximumHeight) this.captureLeaderboard();
        window.clearInterval(timerId);
        return;
      }

      elapsed += 100;
      if (elapsed > resizeWaitDuration * 1000) {
        window.clearInterval(timerId);
      }
    };

    timerId = window.setInterval(waitForHeight);
    waitForHeight();
  }

  captureLeaderboard() {
    const { leaderboardSelector, containerSelector, duration, position } = this._configuration;

    if (this.captured) return;

    const leaderboardContainer = document.querySelector(containerSelector);
    const leaderboard = document.querySelector(leaderboardSelector);

    const leaderboardHeight = leaderboard.offsetHeight;
    leaderboardContainer.classList.add('fixed-leaderboard');
    const marginPosition = position === 'bottom' ? 'marginBottom' : 'marginTop';
    document.body.style[marginPosition] = `${leaderboardHeight}px`;

    this.captured = true;

    consolv.log(`MCP.FixedLeaderboard: Enabled for ${duration} seconds`);

    if (duration > 0) window.setTimeout(this.releaseLeaderboard, duration * 1000);
  }

  releaseLeaderboard() {
    const { containerSelector, position } = this._configuration;
    const leaderboardContainer = document.querySelector(containerSelector);

    if (!this.captured || !leaderboardContainer) return;

    leaderboardContainer.classList.remove('fixed-leaderboard');
    const marginPosition = position === 'bottom' ? 'margin-bottom' : 'margin-top';
    document.body.style.removeProperty(marginPosition);

    this.captured = false;

    consolv.log('MCP.FixedLeaderboard: Released leaderboard');
  }
}
